import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { normalizePermissionAccessType } from "permissions/_views/tables/_utilities/permission-filter-search-params";
import { CATEGORY_OPTIONS, type CategoryLabel, type CategoryLabels } from "permissions/_views/permission-table-view-model";
import { ResourceCategory } from "permissions/resources/permission-resource-types";

export type PermissionsViewMode = "create-template" | "details" | "grantee" | "home" | "matrix" | "templates";

/**
 * Route parameters which intentionally move the permissions page out of the
 * default detailed browser and into a specialized matrix or focused editor.
 */
export const matrixSearchParameterNames = [
  "hasParent",
  "category",
  "categories",
  "granteeUserIds",
  "accessType",
  "documentIds",
  "forumIds",
  "proposalIds",
  "workAgreementIds",
  "resource",
  "grantorUserId",
  "parentId",
  "parentResource",
  "legalEntityId",
  "showDisabled",
  "minCreatedDate",
  "maxCreatedDate",
  "minDisabledDate",
  "maxDisabledDate",
] as const;

const permissionFilterSearchParameterNames = [
  ...matrixSearchParameterNames,
  // Legacy, no-longer-supported URL filter. It is still a permissions-owned
  // key, so clearing filters must not leave it behind.
  "childAccessTypes",
  "name",
] as const;

const mainPermissionsViewSearchParameterNames = [
  "action",
  "view",
  "granteeUserId",
  ...permissionFilterSearchParameterNames,
  "sortBy",
  "sortDir",
  "pageIndex",
  "pageSize",
] as const;

const categoryLabels = new Set<string>(CATEGORY_OPTIONS.map((option) => option.label));
const resourceCategories = new Set<string>(
  Object.keys(ResourceCategory).filter((category) => Number.isNaN(Number(category))),
);

export type PermissionsRouteState = {
  activeCategories: CategoryLabels;
  accessType?: string;
  categories: string[];
  documentIds: Guid[];
  forumIds: Guid[];
  grantorUserId?: Guid;
  granteeUserId?: Guid;
  granteeUserIds: Guid[];
  hasParent?: boolean;
  legalEntityId?: Guid;
  maxCreatedDate?: AHDate;
  maxDisabledDate?: AHDate;
  minCreatedDate?: AHDate;
  minDisabledDate?: AHDate;
  parentId?: Guid;
  parentResource?: string;
  proposalIds: Guid[];
  resource?: string;
  showDisabled?: boolean;
  view?: "details";
  workAgreementIds: Guid[];
};

function toSearchParams(search: string | URLSearchParams): URLSearchParams {
  return typeof search === "string" ? new URLSearchParams(search) : new URLSearchParams(search.toString());
}

export function isNonEmptyPermissionGuid(value: string | null | undefined): value is string {
  return Boolean(value && Guid.isValid(value));
}

function readFirstGuid(params: URLSearchParams, name: string): Guid | undefined {
  const value = params.getAll(name).map((candidate) => candidate.trim()).find(isNonEmptyPermissionGuid);
  return value ? new Guid(value) : undefined;
}

function readGuidList(params: URLSearchParams, name: string): Guid[] {
  const values = new Set(
    params
      .getAll(name)
      .map((value) => value.trim().toLowerCase())
      .filter(isNonEmptyPermissionGuid),
  );

  return [...values].map((value) => new Guid(value));
}

function readFirstString(params: URLSearchParams, name: string): string | undefined {
  return params.getAll(name).map((value) => value.trim()).find(Boolean);
}

function readBoolean(params: URLSearchParams, name: string): boolean | undefined {
  const value = params.getAll(name).map((candidate) => candidate.trim()).find((candidate) => {
    return candidate === "true" || candidate === "false";
  });

  return value === "true" ? true : value === "false" ? false : undefined;
}

function readPermissionsView(params: URLSearchParams): "details" | undefined {
  return params
    .getAll("view")
    .map((candidate) => candidate.trim().toLowerCase())
    .find((candidate): candidate is "details" => candidate === "details");
}

function readDate(params: URLSearchParams, name: string): AHDate | undefined {
  const value = readFirstString(params, name);
  if (!value) return undefined;

  try {
    return AHDate.fromJSON(value) ?? undefined;
  } catch {
    return undefined;
  }
}

function readCategoriesForVisibility(params: URLSearchParams): CategoryLabels {
  return [
    ...new Set(
      params
        .getAll("category")
        .map((candidate) => candidate.trim())
        .filter((candidate): candidate is CategoryLabel => categoryLabels.has(candidate)),
    ),
  ];
}

function readCategories(params: URLSearchParams): string[] {
  return [
    ...new Set(
      params
        .getAll("categories")
        .map((category) => category.trim())
        .filter((category) => resourceCategories.has(category)),
    ),
  ];
}

function replaceValues(params: URLSearchParams, name: string, values: readonly string[]): void {
  params.delete(name);
  for (const value of values) {
    params.append(name, value);
  }
}

function replaceOptionalValue(params: URLSearchParams, name: string, value: string | undefined): void {
  replaceValues(params, name, value ? [value] : []);
}

/**
 * Parses all permissions-owned URL values at one boundary. Callers receive
 * typed, validated values instead of each view making its own loose string
 * interpretation.
 */
export function parsePermissionsRouteState(search: string | URLSearchParams): PermissionsRouteState {
  const params = toSearchParams(search);
  const granteeUserId = readFirstGuid(params, "granteeUserId");
  const parentId = readFirstGuid(params, "parentId");
  // The permissions API treats parentId and parentResource as mutually
  // exclusive. Prefer the concrete parent ID when a stale link contains both.
  const parentResource = parentId ? undefined : readFirstString(params, "parentResource");
  const requestedHasParent = readBoolean(params, "hasParent");
  const requestedView = readPermissionsView(params);
  const documentIds = readGuidList(params, "documentIds");
  const forumIds = readGuidList(params, "forumIds");
  const proposalIds = readGuidList(params, "proposalIds");
  const workAgreementIds = readGuidList(params, "workAgreementIds");
  const hasTargetFilters = documentIds.length > 0 || forumIds.length > 0 || proposalIds.length > 0 || workAgreementIds.length > 0;
  // Parent filters only make sense for inherited permissions. A specific
  // target may be either a manually granted permission or an inherited one,
  // so target deep links select the detailed browser without changing their
  // backend hasParent filter.
  const hasParent = parentId || parentResource ? true : requestedHasParent;
  const view = requestedView ?? (hasTargetFilters ? "details" : undefined);

  return {
    activeCategories: readCategoriesForVisibility(params),
    accessType: normalizePermissionAccessType(readFirstString(params, "accessType") ?? null) ?? undefined,
    categories: readCategories(params),
    documentIds,
    forumIds,
    grantorUserId: readFirstGuid(params, "grantorUserId"),
    granteeUserId,
    // A focused route always owns the grantee selection. Keeping the list
    // empty here makes it impossible for callers to accidentally combine the
    // single-person editor with a multi-person matrix filter.
    granteeUserIds: granteeUserId ? [] : readGuidList(params, "granteeUserIds"),
    hasParent,
    legalEntityId: readFirstGuid(params, "legalEntityId"),
    maxCreatedDate: readDate(params, "maxCreatedDate"),
    maxDisabledDate: readDate(params, "maxDisabledDate"),
    minCreatedDate: readDate(params, "minCreatedDate"),
    minDisabledDate: readDate(params, "minDisabledDate"),
    parentId,
    parentResource,
    proposalIds,
    resource: readFirstString(params, "resource"),
    showDisabled: readBoolean(params, "showDisabled"),
    view,
    workAgreementIds,
  };
}

/**
 * Canonicalizes permissions-owned query values while leaving unrelated route
 * state untouched. This lets links keep future/foreign parameters without
 * allowing invalid values to leak into API requests or UI state.
 */
export function normalizePermissionsRouteSearch(search: string | URLSearchParams): string {
  const params = toSearchParams(search);
  const state = parsePermissionsRouteState(params);

  replaceValues(params, "category", state.activeCategories);
  replaceOptionalValue(params, "accessType", state.accessType);
  replaceOptionalValue(params, "view", state.view);
  replaceValues(params, "categories", state.categories);
  replaceValues(params, "documentIds", state.documentIds.map((id) => id.value));
  replaceValues(params, "forumIds", state.forumIds.map((id) => id.value));
  replaceOptionalValue(params, "grantorUserId", state.grantorUserId?.value);
  replaceOptionalValue(params, "granteeUserId", state.granteeUserId?.value);
  replaceValues(params, "granteeUserIds", state.granteeUserIds.map((id) => id.value));
  replaceOptionalValue(params, "hasParent", state.hasParent === undefined ? undefined : String(state.hasParent));
  replaceOptionalValue(params, "legalEntityId", state.legalEntityId?.value);
  replaceOptionalValue(params, "maxCreatedDate", state.maxCreatedDate?.toISOString());
  replaceOptionalValue(params, "maxDisabledDate", state.maxDisabledDate?.toISOString());
  replaceOptionalValue(params, "minCreatedDate", state.minCreatedDate?.toISOString());
  replaceOptionalValue(params, "minDisabledDate", state.minDisabledDate?.toISOString());
  replaceOptionalValue(params, "parentId", state.parentId?.value);
  replaceOptionalValue(params, "parentResource", state.parentResource);
  replaceValues(params, "proposalIds", state.proposalIds.map((id) => id.value));
  replaceOptionalValue(params, "resource", state.resource);
  replaceOptionalValue(params, "showDisabled", state.showDisabled === undefined ? undefined : String(state.showDisabled));
  replaceValues(params, "workAgreementIds", state.workAgreementIds.map((id) => id.value));

  // The service has no name filter. Remove stale URLs here rather than
  // allowing writers and readers to disagree about whether it exists.
  params.delete("name");

  return params.toString();
}

export function hasMeaningfulPermissionMatrixState(state: PermissionsRouteState): boolean {
  return Boolean(
    state.hasParent ||
      state.activeCategories.length ||
      state.categories.length ||
      state.granteeUserIds.length ||
      state.accessType ||
      state.documentIds.length ||
      state.forumIds.length ||
      state.proposalIds.length ||
      state.workAgreementIds.length ||
      state.resource ||
      state.grantorUserId ||
      state.parentId ||
      state.parentResource ||
      state.legalEntityId ||
      state.showDisabled ||
      state.minCreatedDate ||
      state.maxCreatedDate ||
      state.minDisabledDate ||
      state.maxDisabledDate,
  );
}

/**
 * Indicates whether the current URL has a clearable permissions filter.
 * Focused-grantee and template route state are intentionally excluded so the
 * existing Back behavior remains the only way to leave those views.
 */
export function hasPermissionRouteFilters(search: string | URLSearchParams): boolean {
  return hasMeaningfulPermissionMatrixState(parsePermissionsRouteState(search));
}

/**
 * Keeps template management and focused-editor routes deterministic before
 * considering a matrix filter. Invalid or no-op values do not force the
 * heavier matrix view.
 */
export function getPermissionsViewMode(search: string | URLSearchParams): PermissionsViewMode {
  const params = toSearchParams(search);
  const state = parsePermissionsRouteState(params);

  if (params.get("action") === "create-template") return "create-template";
  if (params.get("action") === "manage-templates") return "templates";
  if (params.get("action") === "manage-general") return "matrix";
  // An explicit `view=details` deliberately opens the flat browser even for a
  // focused grantee (e.g. the "View specific permissions" action). A details
  // view only *implied* by a target deep link must not override a grantee focus.
  if (readPermissionsView(params) === "details") return "details";
  if (state.granteeUserId) return "grantee";
  if (state.view === "details") return "details";
  if (hasMeaningfulPermissionMatrixState(state)) return "matrix";
  if (!params.toString()) return "home";

  return "details";
}

/** Removes table filters, resets to the first result page, and retains foreign parameters. */
export function clearPermissionRouteFilters(search: string | URLSearchParams): string {
  const params = toSearchParams(search);
  for (const key of permissionFilterSearchParameterNames) {
    params.delete(key);
  }
  params.set("pageIndex", "0");
  return params.toString();
}

/**
 * Returns to the main detailed route by clearing every permissions-owned
 * route key, while preserving unrelated query parameters.
 */
export function clearToMainPermissionsView(params: URLSearchParams): void {
  for (const key of mainPermissionsViewSearchParameterNames) {
    params.delete(key);
  }
}

export { permissionFilterSearchParameterNames, mainPermissionsViewSearchParameterNames };
